import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { Box, Typography, Button } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import AddIcon from '@mui/icons-material/Add';
import { DateTime } from 'luxon';
import { TeamCrest } from './team-crest';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { createEvent } from '../../services/event-services';
import { useAuth } from '../../hooks/useAuth';
import { toast } from 'react-toastify';
import { CssTextField } from '../layout/elements';

/* ------------------------------------------------------------------ *
 * BWF · Create event form — "Match Card" styling. Same submit logic;
 * adds a live matchup preview that resolves flags as you type a
 * recognised country name.
 * ------------------------------------------------------------------ */

const ORANGE = '#ff9800';
const LINE = '1px solid rgba(255,255,255,0.08)';

// Left-aligned field variant on the app's orange CssTextField.
const FormField = styled(CssTextField)({
  width: '100%',
  '& .MuiInputBase-input': { textAlign: 'left', color: '#fff', fontSize: '1rem' },
  '& .MuiOutlinedInput-root': { borderRadius: 11, background: '#161616' },
});


export default function EventForm() {
  const { authData } = useAuth();
  const { state } = useLocation();
  const { group } = state;
  const navigate = useNavigate();
  const [team1, setTeam1] = useState('');
  const [team2, setTeam2] = useState('');
  const [time, setTime] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const format = "yyyy-MM-dd'T'HH:mm";
    const utcTime = DateTime.fromJSDate(time).toUTC().toFormat(format);
    const dataToSend = { team1, team2, time: utcTime, group: group.id };
    const eventData = await createEvent(authData.token, dataToSend);
    if (eventData) {
      toast.success('Event created');
      navigate('/details/' + group.id);
    } else {
      toast.error('Error creating event. Please try again later');
    }
  };

  const canSubmit = team1 && team2 && time;

  return (
    <Box sx={{ textAlign: 'left' }}>
      {/* top bar */}
      <Link to={`/details/${group.id}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: ORANGE, background: 'rgba(255,152,0,0.10)', border: '1px solid rgba(255,152,0,0.25)', borderRadius: 20, padding: '5px 12px 5px 6px', margin: '4px 0 18px', textDecoration: 'none', fontWeight: 600, fontSize: 13 }}>
        <ChevronLeftIcon sx={{ fontSize: 18 }} />
        {group.name}
      </Link>

      {/* heading */}
      <Box sx={{ mb: 2.75 }}>
        <Typography sx={{ fontSize: 26, fontWeight: 800, color: '#fff', fontFamily: '"Archivo", Inter, sans-serif', letterSpacing: '-0.3px' }}>
          New event
        </Typography>
        <Typography sx={{ fontSize: 13.5, color: '#b0b0b0', mt: 0.5 }}>Add a fixture for friends to predict.</Typography>
      </Box>

      {/* live matchup preview */}
      <Box sx={{ background: '#1e1e1e', border: LINE, borderRadius: '16px', p: '18px 16px', mb: 2.5 }}>
        <Typography sx={{ textAlign: 'center', fontSize: 11, fontWeight: 700, letterSpacing: 1.2, textTransform: 'uppercase', color: ORANGE, mb: 2 }}>
          Preview
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <TeamCrest name={team1 || 'Home'} size={52} />
          <Typography sx={{ fontFamily: '"Archivo", Inter, sans-serif', fontSize: 26, fontWeight: 800, color: '#7a7a7a' }}>VS</Typography>
          <TeamCrest name={team2 || 'Away'} size={52} />
        </Box>
      </Box>

      {/* form */}
      <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <FormField label="Home team" value={team1} onChange={(e) => setTeam1(e.target.value)} />
        <FormField label="Away team" value={team2} onChange={(e) => setTeam2(e.target.value)} />

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#b0b0b0', pl: 0.5 }}>
            Kickoff date &amp; time
          </Typography>
          <DatePicker
            selected={time}
            onChange={(date) => setTime(date)}
            showTimeSelect
            timeFormat="HH:mm"
            timeIntervals={5}
            dateFormat="dd MMM yyyy, HH:mm"
            timeCaption="Time"
            withPortal
            placeholderText="Select date and time"
            customInput={
              <Box
                component="button"
                type="button"
                sx={{
                  width: '100%', textAlign: 'left', background: '#161616', color: time ? '#fff' : '#7a7a7a',
                  border: '1px solid rgba(255,152,0,0.5)', borderRadius: '11px',
                  p: '14px 16px', fontSize: '1rem', cursor: 'pointer', fontFamily: 'inherit',
                  '&:hover': { borderColor: '#ff9800' },
                }}
              >
                {time ? DateTime.fromJSDate(time).toFormat('dd MMM yyyy, HH:mm') : 'Select date and time'}
              </Box>
            }
          />
        </Box>

        <Button
          type="submit"
          variant="contained"
          color="primary"
          disabled={!canSubmit}
          startIcon={<AddIcon />}
          sx={{ mt: 1, py: 1.4, fontSize: 15, fontWeight: 700, borderRadius: '12px' }}
        >
          Create event
        </Button>
      </Box>
    </Box>
  );
}
